/**
 * health/HealthMonitor.ts — engine health, heartbeats, and recovery.
 *
 * Roamer Core™ probes every registered engine's `healthCheck()`. On failure it
 * follows the recovery ladder: restart if possible → fall back gracefully
 * (disable further work, keep the platform alive) → log → notify only when the
 * engine could not be recovered. Never crashes the platform for one engine.
 */

import type { EngineRegistry } from '../registry/EngineRegistry';

export type RecoveryStatus = 'healthy' | 'recovered' | 'degraded' | 'unrecovered';

export interface EngineHealthRecord {
  engineId: string;
  healthy: boolean;
  lastHeartbeat: number | null;
  lastResponseTimeMs: number | null;
  failureCount: number;
  consecutiveFailures: number;
  recoveryStatus: RecoveryStatus;
  lastError: string | null;
}

export interface HealthCheckOutcome {
  engineId: string;
  healthy: boolean;
  /** true when a failed engine was restarted successfully this round. */
  recovered: boolean;
  /** true when the engine failed AND could not be recovered — notify. */
  needsAttention: boolean;
}

export class HealthMonitor {
  private records = new Map<string, EngineHealthRecord>();

  constructor(private registry: EngineRegistry) {}

  private ensure(engineId: string): EngineHealthRecord {
    let rec = this.records.get(engineId);
    if (!rec) {
      rec = {
        engineId,
        healthy: true,
        lastHeartbeat: null,
        lastResponseTimeMs: null,
        failureCount: 0,
        consecutiveFailures: 0,
        recoveryStatus: 'healthy',
        lastError: null,
      };
      this.records.set(engineId, rec);
    }
    return rec;
  }

  /** Probe one engine; attempt restart-recovery on failure. */
  async checkEngine(engineId: string, now: number): Promise<HealthCheckOutcome> {
    const entry = this.registry.get(engineId);
    const rec = this.ensure(engineId);
    if (!entry || !entry.enabled) {
      return { engineId, healthy: rec.healthy, recovered: false, needsAttention: false };
    }

    const started = Date.now();
    let healthy = true;
    let error: string | null = null;
    try {
      const result = entry.adapter.healthCheck ? await entry.adapter.healthCheck() : true;
      healthy = result !== false;
      if (!healthy) error = 'healthCheck returned false';
    } catch (err) {
      healthy = false;
      error = err instanceof Error ? err.message : String(err);
    }
    rec.lastResponseTimeMs = Math.max(0, Date.now() - started);
    rec.lastHeartbeat = now;

    if (healthy) {
      const wasUnhealthy = !rec.healthy;
      rec.healthy = true;
      rec.consecutiveFailures = 0;
      rec.lastError = null;
      rec.recoveryStatus = wasUnhealthy ? 'recovered' : 'healthy';
      this.registry.setStatus(engineId, 'running');
      return { engineId, healthy: true, recovered: wasUnhealthy, needsAttention: false };
    }

    // Failure path — restart if possible, degrade gracefully otherwise.
    rec.healthy = false;
    rec.failureCount += 1;
    rec.consecutiveFailures += 1;
    rec.lastError = error;
    this.registry.setStatus(engineId, 'failed');

    const restarted = await this.registry.restart(engineId);
    if (restarted) {
      // Re-probe once after restart.
      try {
        const ok = entry.adapter.healthCheck ? await entry.adapter.healthCheck() : true;
        if (ok !== false) {
          rec.healthy = true;
          rec.consecutiveFailures = 0;
          rec.recoveryStatus = 'recovered';
          this.registry.setStatus(engineId, 'running');
          return { engineId, healthy: true, recovered: true, needsAttention: false };
        }
      } catch {
        // fall through to degraded
      }
    }

    // Restart didn't help (or the re-probe failed): the registry must agree
    // with the health record — never leave a still-unhealthy engine 'running'.
    this.registry.setStatus(engineId, 'failed');
    rec.recoveryStatus = rec.consecutiveFailures >= 3 ? 'unrecovered' : 'degraded';
    // Notify only when it genuinely cannot recover.
    return {
      engineId,
      healthy: false,
      recovered: false,
      needsAttention: rec.recoveryStatus === 'unrecovered',
    };
  }

  /** Probe every enabled engine. */
  async checkAll(now: number): Promise<HealthCheckOutcome[]> {
    const outcomes: HealthCheckOutcome[] = [];
    for (const info of this.registry.list()) {
      outcomes.push(await this.checkEngine(info.id, now));
    }
    return outcomes;
  }

  get(engineId: string): EngineHealthRecord | null {
    const rec = this.records.get(engineId);
    return rec ? { ...rec } : null;
  }

  list(): EngineHealthRecord[] {
    return Array.from(this.records.values()).map((r) => ({ ...r }));
  }
}
